import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    IconButton,
    CircularProgress,
    Tooltip,
    Button
} from '@mui/material';
import { ArrowUpward } from '@material-ui/icons';
import EditIcon from '@material-ui/icons/Edit';
import { CHAT_TYPE } from '../../../../../utils/constants';
import { AUDIENCES } from '../../data/learnCreatorCatalog';
import { CONTENT_STYLES } from '../../../../../utils/dq_program_constants';
import RazProgressSVG from "../../../../../components/UI/SVG_IMGs/RazProgressSVG";
import FloatingNudge from "../../../../../components/FloatingNudge/FloatingNudge.comp";
import { parseMarkdown, stripMarkers } from '../../utils/chat.utils';
import { canBuy, paysInDqt, topUpLabel, shortfallLine, askLine } from '../../utils/credit_shortfall.utils';
import renderDqAiMarkdown, { QUESTION_BOX_STYLE } from '../../../DqAiPreview/utils/dqai_markdown';

// Shared copy for the Edit Tool hover tooltip and the one-time showcase nudge.
const EDIT_TOOL_HINT = "If you would like to edit your program, click this button. You can change visual styles, connect with specific school subjects, adjust content to local sensitivities and more.";

const FRAMEWORKS_PREVIEW_COUNT = 3;
const IEEE_STANDARDS_FRAMEWORK = { value: 'ieee_standards_default', label: 'IEEE Standards', DQCO: [] };

const FrameworksMessage = ({ frameworks, classes }) => {
    const [expanded, setExpanded] = useState(false);
    const hasIEEE = frameworks.some(f => f.label === 'IEEE Standards');
    const allFrameworks = hasIEEE ? frameworks : [IEEE_STANDARDS_FRAMEWORK, ...frameworks];
    const visible = expanded ? allFrameworks : allFrameworks.slice(0, FRAMEWORKS_PREVIEW_COUNT);
    const hiddenCount = allFrameworks.length - FRAMEWORKS_PREVIEW_COUNT;

    return (
        <Box className={classes.frameworksMsgWrap}>
            <Box sx={{ fontSize: '13px', color: 'var(--brand-text, #333)', lineHeight: 1.5, mb: 0.5 }}>
                Your program will be aligned with the following standards and frameworks. You can review or change these alignments after the program is created.
            </Box>
            <Box className={classes.frameworksPillsRow}>
                {visible.map(fw => (
                    <Box key={fw.value} className={classes.frameworkPill}>{fw.label}</Box>
                ))}
                {!expanded && hiddenCount > 0 && (
                    <button className={classes.frameworkMoreBtn} onClick={() => setExpanded(true)}>
                        +{hiddenCount} more ▾
                    </button>
                )}
                {expanded && (
                    <button className={classes.frameworkMoreBtn} onClick={() => setExpanded(false)}>
                        ▴ show less
                    </button>
                )}
            </Box>
        </Box>
    );
};

const TIME_OPTIONS = [
    { label: '30 minutes × 1 session',                    message: '30 minutes, 1 session'  },
    { label: '45 minutes × 1 session',                    message: '45 minutes, 1 session'  },
    { label: '45 minutes × 4 sessions — 3 hours total',   message: '45 minutes, 4 sessions' },
    { label: '45 minutes × 8 sessions — 6 hours total',   message: '45 minutes, 8 sessions' },
];

/**
 * The steps whose options live in the sentence rather than in a list.
 *
 * Only consulted when the reply carries no bullets or table, so a run where
 * the assistant DOES list them keeps following the server. Keyed on the
 * server's stage, so rewording a question cannot switch the chips off.
 */
const STAGE_OPTIONS = {
    // The seven bands the curation engine recognises.
    audience: AUDIENCES.map((a) => `${a.label} (ages ${a.ages})`),
    // Asked after "Custom number"; the engine accepts 1-10.
    duration: Array.from({ length: 10 }, (_, i) => `${i + 1} session${i ? 's' : ''}`),
    curriculum: ['Standalone program', 'Integrate into a subject'],
    /*
     * English is the default and is never listed with the translations. The five
     * translations are named here too: they were previously read out of the
     * sentence 'translate it into Korean, Japanese, ...', which is the one fixed
     * option set that depended on the wording of the reply.
     *
     * Mirrors ALLOWED_LANGUAGES in my-dq-api/src/services/dq_ai/constants.dq_ai.js.
     */
    languages: ['Keep it in English', 'Korean', 'Japanese', 'Thai', 'Vietnamese', 'Spanish'],
    /*
     * The engine treats an empty list as "the person confirmed none", so this
     * is a real answer rather than a way of skipping the question — and it was
     * the only route through this step that needed typing.
     */
    sensitivity: ['No specific sensitivities'],
};

/**
 * Choices written into the sentence: "such as A, B, or C".
 *
 * Deliberately narrow — it fires only on the connectives the assistant
 * actually uses to enumerate ("such as", "like", "translate it into"), and
 * only when the tail parses into several short items. Anything longer than a
 * few words is prose that happened to contain a comma.
 */
const extractProseOptions = (text, maxWords = 5) => {
    const raw = String(text || '');

    /*
     * Two ways the assistant introduces a list:
     *
     *   "...such as A, B, or C"
     *   "...I should account for — A, B, C, or D?"
     *
     * The dash form carries no lead-in words at all, so it is matched on the
     * dash itself. It is taken only when the tail actually reads as a list —
     * the comma count is checked below — so a sentence that merely contains a
     * dash does not become buttons.
     */
    let m = raw.match(/(?:such as|translate it into|for example,?|like)\s+([^.?!]+)/i);
    /*
     * "like" introduces a list ("a core focus like cyberbullying prevention,
     * digital privacy...") but also appears inside "would you LIKE to build
     * on", where reading the tail as options produced the chips "To build on"
     * and "No formal alignment" — neither an option.
     *
     * What separates them is the word after it: a verb continues the
     * sentence, a noun starts the list. Requiring a comma before "like" was
     * the first attempt and it cost the greeting its chips, since that one
     * reads "a core focus like ..." with no comma at all.
     */
    if (m && /^to\s/i.test(m[1])) m = null;
    // Anchored on the QUESTION mark rather than any sentence end: this reply
    // opens with a dash clause of its own ("...to fit your context — how people
    // are shown, ..."), and matching that one hands back the description as if
    // it were the menu.
    if (!m) m = raw.match(/[\u2014\u2013-]\s+([^.?!]*,[^.?!]*)\?/);
    if (!m) return [];
    return m[1]
        .split(/,|\bor\b/i)
        // Strip the lead-in that introduces the list. Without it the FIRST item
        // carries "this could relate to" into its word count and is dropped as
        // prose, leaving a partial set of examples.
        .map((p) => p
            .replace(/^(this could (relate to|mean|involve)|tell me it.s for|it.s for)\s+/i, '')
            .replace(/^the\s+/i, '')
            // A list that runs into a trailing clause ("...balanced screen habits
            // — or suggest an area of your own") leaves the dash on the last item.
            .replace(/[\u2014\u2013-]\s*$/, '')
            .trim())
        .filter((p) => p && p.length <= 8 * maxWords && p.split(/\s+/).length <= maxWords)
        // Sentence-case, because mid-sentence fragments arrive lower-cased.
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1));
};

/**
 * Pull the choices out of a bot reply, so they can be offered as chips.
 *
 * Reads what the server actually listed rather than keeping a second copy of
 * every option list on the client — the two would drift, and the server is the
 * one that decides what is on offer.
 *
 * Bullets:  `- **Label** — description`  (also • and *, en/em dash or hyphen)
 * Tables:   the first cell of each data row (the competency ID, the framework
 *           name) — which is what the assistant asks you to reply with.
 *
 * Returns [] when it finds fewer than two, so ordinary prose never sprouts
 * chips. The caller also renders them only on the newest message.
 */
const extractReplyOptions = (raw) => {
    const text = String(raw || '');
    if (!text.trim()) return [];

    const out = [];
    const seen = new Set();
    const push = (label) => {
        const clean = String(label || '')
            /*
             * A framework cell is a link, not a name:
             *   [Global Kids Online Survey Guide](http://globalkidsonline.net/...)
             * Taken whole it blows past the length cap and the row is dropped, so
             * the link text is what becomes the chip — and what gets sent back,
             * which is the name the assistant asked for anyway.
             */
            .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
            .replace(/\*\*/g, '')
            .trim();
        // A chip has to be short enough to read at a glance; anything longer is
        // prose that happened to start with a bullet.
        if (!clean || clean.length > 60) return;
        const key = clean.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        out.push(clean);
    };

    const lines = text.split(/\r?\n/);

    for (const line of lines) {
        const t = line.trim();
        if (!t) continue;

        // ── a table row ──
        // Two or more pipes, rather than a leading one: markdown tables are
        // written both with and without the outer pipes, and a row is a row
        // either way. Two pipes is still specific enough that ordinary prose
        // does not qualify.
        if ((t.match(/\|/g) || []).length >= 2) {
            const cells = t.split('|').map((c) => c.trim()).filter((c) => c !== '');
            if (!cells.length) continue;
            const first = cells[0];
            // Separator row (|---|---|) and the header row are not options.
            if (/^:?-{2,}:?$/.test(first)) continue;
            if (/^(id|framework|name|competency)$/i.test(first)) continue;
            push(first);
            continue;
        }

        // ── a bullet ──
        const bullet = t.match(/^[-*\u2022]\s+(.*)$/);
        if (!bullet) continue;
        const body = bullet[1];
        // "Label — description": the label is what gets sent. Without a dash the
        // whole bullet is the label.
        const split = body.split(/\s[\u2014\u2013-]\s/);
        push(split[0]);
    }

    return out.length >= 2 ? out : [];
};

/**
 * Which options to offer for the reply on screen.
 *
 * A reply that LISTED its choices always wins — that is the server saying
 * outright what is on offer. Only when it asked in prose do the other two
 * sources come in: the stage's known set, then anything the sentence named.
 *
 * languages takes both, because "keep it in English" is the default and is
 * never written into the list of translations beside it.
 */
const resolveReplyOptions = (text, stage) => {
    const listed = extractReplyOptions(text);
    if (listed.length) return listed;

    const known = STAGE_OPTIONS[stage] || [];
    /*
     * Sensitivities are described in longer phrases than any other step
     * (a phrase like "policies around device access at school"), so the
     * reader is allowed a wider item here. Raising it everywhere would let
     * ordinary prose with commas turn into buttons.
     */
    const prose = extractProseOptions(text, stage === 'sensitivity' ? 7 : 5);
    /*
     * Two steps take the known answer AND what the sentence named: languages,
     * where "keep it in English" is the default and never listed beside the
     * translations; and sensitivities, where "none" is an answer in its own
     * right alongside the examples offered.
     */
    const merged = (stage === 'languages' || stage === 'sensitivity')
        ? [...known, ...prose]
        : (known.length ? known : prose);

    const seen = new Set();
    const out = [];
    for (const o of merged) {
        const key = o.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(o);
    }
    return out.length >= 2 ? out : [];
};

/**
 * The chips for one bot reply, and whether the step takes several answers.
 *
 * The server sends the choices for the multi-select steps (competencies,
 * frameworks, translation languages, sensitivities) as reply_options, read
 * from the turn itself. Every other step — and a transcript saved before the
 * server sent them — still reads the choices out of the reply text.
 *
 * @returns {{ items: {value, label, hint?, exclusive?}[], multi: boolean, max: number|null }}
 */
const resolveChipSpec = (msg, stage) => {
    const server = msg?.replyOptions;
    if (server && Array.isArray(server.items) && server.items.length) {
        const items = [...server.items];
        // Sensitivities: the examples are written into the question, so the
        // ones read there are added beside the server's "none" answer.
        if (server.extend_from_reply) {
            const seen = new Set(items.map((i) => String(i.value).toLowerCase()));
            for (const label of resolveReplyOptions(msg.text, stage)) {
                if (seen.has(label.toLowerCase())) continue;
                seen.add(label.toLowerCase());
                items.push({ value: label, label });
            }
        }
        // One chip, or a cap of one, is a single pick: one click sends.
        const multi = Boolean(server.multi) && items.length > 1 && server.max !== 1;
        return { items, multi, max: multi ? (server.max ?? null) : 1 };
    }
    const labels = resolveReplyOptions(msg?.text, stage);
    return { items: labels.map((label) => ({ value: label, label })), multi: false, max: null };
};

/**
 * The chips for one bot reply.
 *
 * Single pick (most steps): same interaction as the age and time chips — one
 * click sends, then the row locks so a second click cannot send a
 * contradicting answer while the first is still in flight.
 *
 * Multi-select (multi): the chat box holds the answer. Ticking a chip writes
 * its value into the box ("DQ_IND_13_03, DQ_IND_16_02") and the chat's own
 * send button sends it — no second Send button. The ticks are read back from
 * the box, so deleting a value there unticks its chip, and anything typed
 * beside the values stays. `max` caps how many can be ticked; an `exclusive`
 * item ("Keep it in English") clears the other chips.
 */
const splitAnswer = (text) => String(text || '').split(',').map((t) => t.trim()).filter(Boolean);

const ReplyOptions = ({ items, multi = false, max = null, isConsumed, onSelect, inputText = '', setInputText }) => {
    const [selected, setSelected] = useState(null);
    const consumed = isConsumed || (!multi && selected !== null);

    const itemFor = (token) => items.find((i) => i.value.toLowerCase() === token.toLowerCase());
    const tokens = multi ? splitAnswer(inputText) : [];
    const picked = multi
        ? items.filter((i) => tokens.some((t) => t.toLowerCase() === i.value.toLowerCase())).map((i) => i.value)
        : (selected === null ? [] : [selected]);
    const atMax = multi && max != null && picked.length >= max;

    const handleChip = (item) => {
        if (consumed) return;
        if (!multi) {
            setSelected(item.value);
            onSelect(item.value);
            return;
        }
        let next;
        if (picked.includes(item.value)) {
            next = tokens.filter((t) => t.toLowerCase() !== item.value.toLowerCase());
        } else if (item.exclusive) {
            // Keeps what was typed, drops the other chips.
            next = [...tokens.filter((t) => !itemFor(t)), item.value];
        } else {
            const kept = tokens.filter((t) => !itemFor(t)?.exclusive);
            const count = kept.filter((t) => itemFor(t)).length;
            if (max != null && count >= max) return;
            next = [...kept, item.value];
        }
        setInputText(next.join(', '));
    };

    return (
        <Box sx={{ marginTop: '12px' }}>
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
            }}>
                {items.map((item) => {
                    const { value, label } = item;
                    const isSelected = picked.includes(value);
                    // At the cap, the unticked chips rest until one is unticked.
                    const resting = atMax && !isSelected && !item.exclusive;
                    return (
                        <Box
                            key={value}
                            title={item.hint || undefined}
                            onClick={() => handleChip(item)}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '5px 12px',
                                borderRadius: '999px',
                                fontSize: '12.5px',
                                fontWeight: 600,
                                lineHeight: 1.3,
                                whiteSpace: 'nowrap',
                                cursor: consumed || resting ? 'default' : 'pointer',
                                transition: 'all 0.15s ease',
                                border: '1px solid #FF6900',
                                backgroundColor: isSelected ? '#FF6900' : 'transparent',
                                color: isSelected ? '#fff' : '#FF6900',
                                // A picked chip stays legible; the ones not taken fade
                                // back rather than disappear, so the choice made is
                                // still readable in the transcript.
                                opacity: (consumed || resting) && !isSelected ? 0.4 : 1,
                                pointerEvents: consumed ? 'none' : 'auto',
                                '&:hover': resting ? {} : {
                                    backgroundColor: '#FF6900',
                                    color: '#fff',
                                },
                            }}
                        >
                            {multi && isSelected ? `✓ ${label}` : label}
                        </Box>
                    );
                })}
            </Box>
            {multi && (
                <Typography sx={{ fontSize: '12px', color: 'var(--brand-text-muted, #6B7280)', marginTop: '8px' }}>
                    {max != null ? `Pick up to ${max}` : 'Pick one or more'}, then press send.
                </Typography>
            )}
        </Box>
    );
};

/**
 * The visual-style step, as pictures.
 *
 * The same images and labels as Content Style in Edit Program Information
 * (CONTENT_STYLES), so the chat and the dialog show one set of styles. Each
 * card sends the style's name as the chatbot writes it in its own list
 * ("2D Vector Flat", "Isometric 3D", …) — exactly what the text chip sent — so
 * the conversation behaves as before; only the choice is now visual.
 *
 * Same interaction as the chips: one pick, then the set locks.
 */
/**
 * The style a message names, if any.
 *
 * A picked style is sent as its name ('Claymation'), and the option grid only
 * lives on the newest reply — so once the answer was sent, the picture the
 * person chose was gone. Matching the message back to the catalogue lets the
 * answer keep its thumbnail in the transcript.
 */
const styleForMessage = (text) => {
    const t = String(text || '').trim().toLowerCase();
    if (!t) return null;
    return CONTENT_STYLES.find((c) => (
        c.label.toLowerCase() === t
        || String(c.value).replace(/_/g, ' ').toLowerCase() === t
        || (c.value === 'Infographic_Style' && t === 'infographic')
    )) || null;
};

const styleMessageFor = (value) =>
    // The chatbot lists this one as plain "Infographic".
    value === 'Infographic_Style' ? 'Infographic' : String(value).replace(/_/g, ' ');

const StyleOptions = ({ isConsumed, onSelect }) => {
    const [selected, setSelected] = useState(null);
    const consumed = isConsumed || selected !== null;

    /*
     * The chat scrolls to the newest reply when it arrives, but these images
     * load a moment later and push the grid below the fold — so the question
     * appeared to have no options at all. Bring the grid into view once it
     * renders, and again as each image lands, until a style is picked.
     */
    const gridRef = useRef(null);
    const reveal = () => {
        if (selected !== null || !gridRef.current) return;
        gridRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { reveal(); }, []);

    const pick = (style) => {
        if (consumed) return;
        setSelected(style.value);
        onSelect(styleMessageFor(style.value));
    };

    return (
        <Box ref={gridRef} sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
            gap: '10px',
            marginTop: '12px',
        }}>
            {CONTENT_STYLES.map((style) => {
                const isSelected = selected === style.value;
                return (
                    <Box
                        key={style.value}
                        role="button"
                        tabIndex={consumed ? -1 : 0}
                        aria-label={style.label}
                        onClick={() => pick(style)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(style); } }}
                        sx={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            borderRadius: '10px', overflow: 'hidden',
                            border: `2px solid ${isSelected ? '#FF6900' : 'var(--brand-border, #E0E0E0)'}`,
                            cursor: consumed ? 'default' : 'pointer',
                            transition: 'all 0.15s',
                            // The picked style stays clear; the rest fade, so the choice
                            // is still readable in the transcript.
                            opacity: consumed && !isSelected ? 0.4 : 1,
                            pointerEvents: consumed ? 'none' : 'auto',
                            '&:hover': { borderColor: '#FF6900' },
                        }}
                    >
                        <Box component="img" src={style.image} alt={style.label} onLoad={reveal}
                             sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
                        <Box sx={{
                            fontSize: '12px', fontWeight: isSelected ? 700 : 500, py: '6px', px: '4px',
                            textAlign: 'center', width: '100%',
                            color: isSelected ? '#FF6900' : 'var(--brand-text, #333)',
                            backgroundColor: isSelected ? '#FFF5F0' : 'var(--brand-surface, #fff)',
                        }}>
                            {style.label}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

const TimeOptionsMessage = ({ isConsumed, onSelect, onTypeOwn, classes }) => {
    const [selected, setSelected] = useState(null);
    const consumed = isConsumed || selected !== null;

    const handleChip = (opt) => {
        if (consumed) return;
        setSelected(opt.label);
        onSelect(opt.message);
    };

    const handleTypeOwn = () => {
        if (consumed) return;
        setSelected('TYPE_OWN');
        onTypeOwn();
    };

    return (
        <Box className={classes.timeOptionsMsgWrap}>
            <Box className={classes.timeOptionsMsgTitle}>How long should each session be, and how many sessions would you like to include?</Box>
            <Box className={classes.timeOptionsMsgSubtitle}>Choose an option below or enter a custom format.</Box>
            <Box className={classes.timeOptionsChipsRow}>
                {TIME_OPTIONS.map(opt => {
                    const isSelected = selected === opt.label;
                    return (
                        <Box
                            key={opt.label}
                            className={[
                                classes.timeOptionsChip,
                                isSelected ? classes.timeOptionsChipSelected : '',
                                consumed && !isSelected ? classes.timeOptionsChipDisabled : '',
                            ].join(' ')}
                            onClick={() => handleChip(opt)}
                        >
                            {opt.label}
                        </Box>
                    );
                })}
                <Box
                    className={[
                        classes.timeOptionsChip,
                        classes.timeOptionsChipOwn,
                        selected === 'TYPE_OWN' ? classes.timeOptionsChipSelected : '',
                        consumed && selected !== 'TYPE_OWN' ? classes.timeOptionsChipDisabled : '',
                    ].join(' ')}
                    onClick={handleTypeOwn}
                >
                    ✏️ Custom duration
                </Box>
            </Box>
        </Box>
    );
};

/**
 * Shown when the server sends {{show_age_options=[…]}} — the age-band overlap case.
 * Renders the candidate bands as clickable chips so the user picks ONE instead of
 * typing an ambiguous range. Mirrors TimeOptionsMessage's interaction model.
 */
const AgeOptionsMessage = ({ ageOptions, isConsumed, onSelect, classes }) => {
    const [selected, setSelected] = useState(null);
    const consumed = isConsumed || selected !== null;

    const handleChip = (opt) => {
        if (consumed) return;
        setSelected(opt.value);
        onSelect(opt);
    };

    return (
        <Box className={classes.timeOptionsMsgWrap}>
            <Box className={classes.timeOptionsMsgTitle}>
                To make the content appropriate for your learners, please select one of our available age groups.
            </Box>
            <Box className={classes.timeOptionsChipsRow}>
                {ageOptions.map(opt => {
                    const isSelected = selected === opt.value;
                    return (
                        <Box
                            key={opt.value}
                            className={[
                                classes.timeOptionsChip,
                                isSelected ? classes.timeOptionsChipSelected : '',
                                consumed && !isSelected ? classes.timeOptionsChipDisabled : '',
                            ].join(' ')}
                            onClick={() => handleChip(opt)}
                        >
                            {opt.label}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

const PILL_SX = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    alignSelf: 'flex-start',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 700,
    color: '#fff',
    background: 'linear-gradient(135deg, #FF6900 0%, #FF8C33 100%)',
    border: 'none',
    borderRadius: '20px',
    padding: '6px 14px',
    boxShadow: '0 2px 6px rgba(255,105,0,0.35)',
    transition: 'opacity 0.15s',
    userSelect: 'none',
    '&:hover': { opacity: 0.88 },
};

/**
 * What the builder can do about a short pool. Only the workspace owner can buy, so
 * the owner gets the Top Up that matches what the build is paid in (DQ Tokens for a
 * partner, credits otherwise); anyone else is told whom to ask, with Try again for
 * once they have. Opening Top Up never uses up the card: a payment window closed
 * without paying leaves every button where it was.
 */
const CreditShortfallActions = ({ info, onTopUpCredits, onTryAgain }) => {
    if (info?.unavailable) {
        return <Box sx={{ fontSize: '12px', color: '#C2410C', lineHeight: 1.5 }}>{info.unavailable}</Box>;
    }
    // A card saved before the server named the payer: ask the server again — its
    // answer says who can act.
    if (!info?.payer) {
        return (
            <>
                <Box sx={{ fontSize: '12px', color: '#C2410C', lineHeight: 1.5 }}>
                    Not enough credits to build this program yet. Press Try again to check your balance.
                </Box>
                {onTryAgain && <Box onClick={onTryAgain} sx={PILL_SX}>🔄 Try again</Box>}
            </>
        );
    }
    const need = shortfallLine(info);
    if (canBuy(info.payer)) {
        const fallback = paysInDqt(info) ? "You don't have enough DQ Tokens." : "You don't have enough credits.";
        return (
            <>
                <Box sx={{ fontSize: '12px', color: '#C2410C', lineHeight: 1.5 }}>
                    {need || fallback} {paysInDqt(info) ? 'Top up DQ Tokens to continue.' : 'Top up to continue.'}
                </Box>
                {onTopUpCredits && (
                    <Box onClick={() => onTopUpCredits(info)} sx={PILL_SX}>💳 {topUpLabel(info?.payer)}</Box>
                )}
            </>
        );
    }
    return (
        <>
            <Box sx={{ fontSize: '12px', color: '#C2410C', lineHeight: 1.5 }}>
                {need ? `${need} ` : ''}{askLine(info)}
            </Box>
            {onTryAgain && <Box onClick={onTryAgain} sx={PILL_SX}>🔄 Try again</Box>}
        </>
    );
};

/**
 * The server's insufficient_credits card: the pool paying for the build cannot cover
 * it (at the start of the chat, or when the build is pressed). `info` carries the
 * shortfall and who can act on it; its actions stay until the person answers.
 */
const InsufficientCreditsMessage = ({ info, isConsumed, onTopUpCredits, onTryAgain }) => {
    const pool = info?.payer?.org_name;
    const headline = paysInDqt(info)
        ? "There aren't enough DQ Tokens to build this program yet."
        : pool
            ? `There aren't enough credits in ${pool}'s pool to build this program yet.`
            : "There aren't enough credits to build this program yet.";
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <Box component="span" sx={{ fontSize: '18px', lineHeight: 1.3 }}>⚠️</Box>
                <Box sx={{ fontSize: '13px', color: '#7A3B00', lineHeight: 1.5 }}>{headline}</Box>
            </Box>
            {!isConsumed && (
                <CreditShortfallActions info={info} onTopUpCredits={onTopUpCredits} onTryAgain={onTryAgain} />
            )}
        </Box>
    );
};

/**
 * Shown when the server signals that the program build pipeline failed.
 * Renders an error message and a "Try Again" CTA button.
 */
const BuildFailedMessage = ({ onRetryBuild, phase = 'outline' }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <Box component="span" sx={{ fontSize: '18px', lineHeight: 1.3 }}>❌</Box>
            <Box sx={{ fontSize: '13px', color: '#7A0000', lineHeight: 1.5 }}>
                {phase === 'content'
                    ? 'Program content generation did not complete. Please try again.'
                    : 'Program outline generation did not complete. Please try again.'}
            </Box>
        </Box>
        {onRetryBuild && (
            <Box
                onClick={onRetryBuild}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    alignSelf: 'flex-start',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#fff',
                    background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '6px 14px',
                    boxShadow: '0 2px 6px rgba(211,47,47,0.35)',
                    transition: 'opacity 0.15s',
                    userSelect: 'none',
                    '&:hover': { opacity: 0.88 },
                }}
            >
                🔄 Try Again
            </Box>
        )}
    </Box>
);

/**
 * Shown when the server sends {{confirm_build_cost=…}} — the cost-confirmation gate.
 * Displays the session summary and credit cost, then lets the user Proceed (which
 * starts the build) or make changes. When the pool is short, Proceed is replaced by
 * what this builder can do about it (CreditShortfallActions).
 *
 * The card carries the build decision for the chat: the server moves the chatbot's own
 * closing question ("Would you like to build your program now, or change any of your
 * answers?") onto the card as `question`, and it is shown here in the chat's question
 * style in place of "Would you like to proceed?".
 */
const ConfirmBuildMessage = ({ costInfo, isConsumed, onProceed, onCancel, onTopUpCredits }) => {
    const [clicked, setClicked] = useState(false);
    const consumed = isConsumed || clicked;

    const {
        number_of_sessions,
        session_duration_minutes,
        total_minutes,
        required_credits,
        balance_credits,
        sufficient,
        gated,
        dqt,
        question,
    } = costInfo || {};
    const closingQuestion = question
        ? <Box sx={{ ...QUESTION_BOX_STYLE, fontSize: '13px' }}>{question}</Box>
        : null;

    const hasCost   = gated && required_credits > 0;
    // A partner builds with DQ Tokens: the server sends the DQ Token figures to show instead.
    const inDqt     = !!dqt && typeof dqt.required === 'number';
    const fmtNum    = (n) => Number(n || 0).toLocaleString('en-US');
    const totalHrs  = total_minutes ? Math.round((total_minutes / 60) * 10) / 10 : null;

    const handleProceed = () => { if (consumed) return; setClicked(true); onProceed(); };
    const handleCancel  = () => { if (consumed) return; setClicked(true); onCancel(); };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Box sx={{ fontSize: '14px', fontWeight: 700, color: 'var(--brand-text, #111)' }}>
                Ready to build your program
            </Box>

            {/* Session summary */}
            <Box sx={{ fontSize: '13px', color: 'var(--brand-text, #333)', lineHeight: 1.5 }}>
                {number_of_sessions} session{number_of_sessions > 1 ? 's' : ''} × {session_duration_minutes} min
                {totalHrs ? ` — ${totalHrs} hour${totalHrs === 1 ? '' : 's'} total` : ''}
            </Box>

            {/* Cost line — copy matches the product spec */}
            {hasCost ? (
                <Box sx={{ fontSize: '13px', color: 'var(--brand-text, #333)', lineHeight: 1.5 }}>
                    {inDqt ? (
                        <>
                            Ok this will cost you <strong>{fmtNum(dqt.required)} DQ Tokens</strong> to create.
                            <Box component="span" sx={{ color: 'var(--brand-text-muted, #777)' }}> {' '}(Your balance: {fmtNum(dqt.balance)} DQ Tokens)</Box>
                        </>
                    ) : (
                        <>
                            Ok this will cost you <strong>{required_credits} credits</strong> to create.
                            {typeof balance_credits === 'number' && (
                                <Box component="span" sx={{ color: 'var(--brand-text-muted, #777)' }}> {' '}(Your balance: {balance_credits} credits)</Box>
                            )}
                        </>
                    )}
                    {!question && <><br />Would you like to proceed?</>}
                </Box>
            ) : (
                <Box sx={{ fontSize: '13px', color: 'var(--brand-text, #333)', lineHeight: 1.5 }}>
                    This program is included in your plan — no additional credits.{!question && ' Would you like to proceed?'}
                </Box>
            )}
            {closingQuestion}

            {/*
              * Actions, until one is taken.
              *
              * The card used to stay on screen greyed out after the click, right
              * beside the user message that records the same choice — so the
              * conversation showed what looked like two proceed buttons, one of
              * them dead. The reply is the record; the control has done its job.
              */}
            {!consumed && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                {sufficient ? (
                    <Box
                        onClick={handleProceed}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            cursor: consumed ? 'default' : 'pointer',
                            opacity: consumed ? 0.5 : 1,
                            fontSize: '13px', fontWeight: 700, color: '#fff',
                            background: 'linear-gradient(135deg, #FF6900 0%, #FF8C33 100%)',
                            borderRadius: '20px', padding: '8px 18px',
                            boxShadow: '0 2px 6px rgba(255,105,0,0.35)',
                            transition: 'opacity 0.15s', userSelect: 'none',
                            '&:hover': { opacity: consumed ? 0.5 : 0.88 },
                        }}
                    >
                        ✓ Yes, proceed
                    </Box>
                ) : (
                    // Top Up opens a payment window and leaves the card as it is; Try
                    // again (for those who cannot buy) asks the server to check again.
                    <CreditShortfallActions info={costInfo} onTopUpCredits={onTopUpCredits} onTryAgain={handleProceed} />
                )}
                <Box
                    onClick={handleCancel}
                    sx={{
                        cursor: consumed ? 'default' : 'pointer',
                        opacity: consumed ? 0.5 : 1,
                        fontSize: '13px', fontWeight: 600, color: 'var(--brand-text-muted, #666)',
                        textDecoration: 'underline', userSelect: 'none',
                    }}
                >
                    No, make changes
                </Box>
            </Box>
            )}
        </Box>
    );
};

const ChatSidebar = ({
    classes,
    curationStage,
    PoweredByDQ,
    chatHistory,
    isBotThinking,
    isChatLocked,
    chatInput,
    setChatInput,
    handleChatKeyDown,
    chatInputRef,
    handleSendMessage,
    sendMessage,
    chatEndRef,
    isDevEnv,
    useTemplate,
    onUseTemplateToggle,
    onTopUpCredits,     // (shortfall info) → opens the payment window that matches info.payer
    onRetryCredits,     // asks the server to check the credits again (Try again)
    onConfirmBuild,
    onDeclineBuild,
    onRetryBuild,
    onClose,
    showContent,   // true once the bubble has fully merged — triggers sequential fade-in
    canEditProgram = false, // outline ready + editable + not building
    onEditProgram,          // opens the Edit Program Information dialog
}) => {
    const inputDisabled = isBotThinking || isChatLocked;

    /*
     * The newest bot reply still waiting for an answer — see the note above the
     * transcript for why this is not simply the last entry.
     * When chat is locked, no chips should ever be rendered or active.
     */
    const chipTargetIdx = (() => {
        if (isChatLocked) return -1;
        for (let i = (chatHistory || []).length - 1; i >= 0; i -= 1) {
            const m = chatHistory[i];
            if (!m) continue;
            if (m.type === CHAT_TYPE.USER) return -1;
            // A card answers for itself; its buttons are the interaction.
            if (m.subType) return -1;
            if (String(m.text || '').trim()) return i;
        }
        return -1;
    })();

    // One-time "Edit Tool" showcase: auto-open the hint tooltip the first time a
    // program becomes editable (right after it's generated) so the tool is
    // showcased like a tutorial step, then it behaves as a normal hover tooltip.
    // Gated by localStorage so it only pops once per browser.
    const [editHintOpen, setEditHintOpen] = useState(false);
    useEffect(() => {
        if (!canEditProgram || !onEditProgram) return undefined;
        let seen = false;
        try { seen = !!localStorage.getItem('pbdq_edit_tool_hint_seen'); } catch { /* ignore */ }
        if (seen) return undefined;
        try { localStorage.setItem('pbdq_edit_tool_hint_seen', '1'); } catch { /* ignore */ }
        setEditHintOpen(true);
        const t = setTimeout(() => setEditHintOpen(false), 6000);
        return () => clearTimeout(t);
    }, [canEditProgram, onEditProgram]);

    return (
        <Box className={classes.sidebar}>
            {/* Header — fades in first after bubble merges */}
            <Box style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderBottom: '1px solid var(--brand-border, #F5F1EC)',
                flexShrink: 0,
                width: '100%',
                boxSizing: 'border-box',
                minWidth: 0,
                opacity: showContent ? 1 : 0,
                transition: showContent ? 'opacity 0.15s ease' : 'none',
                pointerEvents: showContent ? 'auto' : 'none',
            }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Box style={{
                        width: '34px', height: '34px', borderRadius: '50%',
                        backgroundColor: 'var(--brand-surface, #fff)', border: '1.5px solid #FFC299',
                        boxShadow: '0 1px 4px rgba(255, 105, 0, 0.18)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, overflow: 'hidden', padding: '5px', boxSizing: 'border-box',
                    }}>
                        <RazProgressSVG color="#FF6900" style={{ width: '100%', height: '100%' }} />
                    </Box>
                    <Box style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--brand-text, #1a1a1a)', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>DQ AI</span>
                        <span style={{ fontSize: '12px', color: 'var(--brand-text-muted, #a3a3a3)', lineHeight: 1.3 }}>Here to help you</span>
                    </Box>
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, marginLeft: '12px' }}>
                    <img src={PoweredByDQ} alt="Powered by DQ" style={{ height: '32px', flexShrink: 0 }} />
                    {isDevEnv && (
                        <Box
                            onClick={onUseTemplateToggle}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                fontSize: '11px',
                                fontWeight: 600,
                                color: useTemplate ? '#FF6900' : 'var(--brand-text-muted, #999)',
                                background: useTemplate ? '#FEF0EC' : 'var(--brand-bg, #F3F4F6)',
                                border: `1px solid ${useTemplate ? '#FF6900' : 'var(--brand-border, #E5E7EB)'}`,
                                borderRadius: '20px',
                                padding: '3px 8px 3px 5px',
                                transition: 'all 0.2s',
                            }}
                            title="Toggle UseTemplate (dev only)"
                        >
                            <Box style={{
                                width: '26px', height: '14px', borderRadius: '7px',
                                background: useTemplate ? '#FF6900' : '#D1D5DB',
                                position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                            }}>
                                <Box style={{
                                    position: 'absolute', top: '2px',
                                    left: useTemplate ? '13px' : '2px',
                                    width: '10px', height: '10px', borderRadius: '50%',
                                    background: '#fff', transition: 'left 0.2s',
                                }} />
                            </Box>
                            {useTemplate ? 'True' : 'False'}
                        </Box>
                    )}
                    {onClose && (
                        <Box
                            onClick={onClose}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--brand-bg, #F3F1EC)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            style={{
                                width: '26px', height: '26px', borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', background: 'transparent', flexShrink: 0,
                                fontSize: '16px', color: 'var(--brand-text-muted, #a3a3a3)', fontWeight: 500,
                                transition: 'background 0.15s',
                            }}
                            title="Minimise chat"
                        >
                            −
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Chat messages — fades in just after the header, not a beat behind it */}
            <Box className={classes.chatContainerWrap} sx={{ opacity: showContent ? 1 : 0, transition: showContent ? 'opacity 0.2s ease 0.05s' : 'none' }}>
                <Box className={classes.chatHistory}>
                    {/*
                      * Which reply gets the chips.
                      *
                      * Walking back from the end rather than taking the last
                      * entry: a cost card, a time-options card or a build-failed
                      * bubble is pushed AFTER the reply it belongs to, and each
                      * of those used to suppress the chips of a perfectly open
                      * question. A restored transcript can end on any of them.
                      *
                      *   USER message  -> answered already, no chips
                      *   card message  -> the card owns the interaction, no chips
                      *                    (its own buttons are the answer)
                      *   text reply    -> the open question, chips here
                      */}
                    {chatHistory.map((msg, idx) => {
                        const isUser = msg.type === CHAT_TYPE.USER;

                        // ── Hidden: build-progress tracking messages (step 1/2/3) ──
                        if (msg.subType === 'build_progress') return null;

                        // ── Hidden: duplicate completion messages or legacy final message that may be in saved history ──
                        if (isChatLocked && !isUser && typeof msg.text === 'string'
                            && (msg.text.startsWith("🎉 Your program has been built") || msg.text.startsWith("Did you know that you can change the graphic style"))) return null;

                        // ── Special: frameworks alignment message ──
                        if (!isUser && msg.subType === 'frameworks' && Array.isArray(msg.frameworks) && msg.frameworks.length > 0) {
                            return (
                                <Box key={idx} className={`${classes.messageRow} ${classes.botRow}`}>
                                    <Box className={classes.botMsgContainer}>
                                        <Box className={classes.botMessage}>
                                            <FrameworksMessage frameworks={msg.frameworks} classes={classes} />
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        }

                        // ── Special: age-options chip card (overlap → pick one) ──
                        if (!isUser && msg.subType === 'age_options' && Array.isArray(msg.ageOptions) && msg.ageOptions.length > 0) {
                            const isConsumed = chatHistory.slice(idx + 1).some(m => m.type === CHAT_TYPE.USER);
                            return (
                                <Box key={idx} className={`${classes.messageRow} ${classes.botRow}`}>
                                    <Box className={classes.botMsgContainer}>
                                        <Box className={classes.botMessage}>
                                            <AgeOptionsMessage
                                                ageOptions={msg.ageOptions}
                                                isConsumed={isConsumed}
                                                onSelect={(opt) => sendMessage(`Set the target age group to ${opt.label} (${opt.value}).`)}
                                                classes={classes}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        }

                        // ── Special: time-options chip card ──
                        if (!isUser && msg.subType === 'time_options') {
                            const isConsumed = chatHistory.slice(idx + 1).some(m => m.type === CHAT_TYPE.USER);
                            return (
                                <Box key={idx} className={`${classes.messageRow} ${classes.botRow}`}>
                                    <Box className={classes.botMsgContainer}>
                                        <Box className={classes.botMessage}>
                                            <TimeOptionsMessage
                                                isConsumed={isConsumed}
                                                onSelect={(msg) => sendMessage(msg)}
                                                onTypeOwn={() => { if (chatInputRef.current) chatInputRef.current.focus(); }}
                                                classes={classes}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        }

                        // ── Special: confirm-build cost card ──
                        if (!isUser && msg.subType === 'confirm_build' && msg.costInfo) {
                            const isConsumed = chatHistory.slice(idx + 1).some(m => m.type === CHAT_TYPE.USER);
                            return (
                                <Box key={idx} className={`${classes.messageRow} ${classes.botRow}`}>
                                    <Box className={classes.botMsgContainer}>
                                        <Box className={classes.botMessage}>
                                            <ConfirmBuildMessage
                                                costInfo={msg.costInfo}
                                                isConsumed={isConsumed}
                                                onProceed={onConfirmBuild}
                                                onCancel={onDeclineBuild}
                                                onTopUpCredits={onTopUpCredits}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        }

                        // ── Special: insufficient-credits card ──
                        if (!isUser && msg.subType === 'insufficient_credits') {
                            const isConsumed = chatHistory.slice(idx + 1).some(m => m.type === CHAT_TYPE.USER);
                            return (
                                <Box key={idx} className={`${classes.messageRow} ${classes.botRow}`}>
                                    <Box className={classes.botMsgContainer}>
                                        <Box
                                            className={classes.botMessage}
                                            sx={{ background: '#FFF3E0 !important', border: '1px solid #FFCC80 !important' }}
                                        >
                                            <InsufficientCreditsMessage
                                                info={msg.shortfallInfo}
                                                isConsumed={isConsumed}
                                                onTopUpCredits={onTopUpCredits}
                                                onTryAgain={onRetryCredits}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        }

                        // ── Special: build failed card ──
                        if (!isUser && msg.subType === 'build_failed') {
                            return (
                                <Box key={idx} className={`${classes.messageRow} ${classes.botRow}`}>
                                    <Box className={classes.botMsgContainer}>
                                        <Box
                                            className={classes.botMessage}
                                            sx={{ background: '#FFEBEE !important', border: '1px solid #FFCDD2 !important' }}
                                        >
                                            <BuildFailedMessage onRetryBuild={onRetryBuild} phase={msg.phase} />
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        }

                        // Bot replies go through the DQ AI renderer: it escapes first and it
                        // understands the tables the assistant uses for competencies and
                        // frameworks, which the older renderer showed as raw pipes.
                        // stripMarkers stays for transcripts saved by the previous engine.
                        const displayedText = isUser
                            ? parseMarkdown(msg.text || '')
                            // Same tables, wider: at 460px the five framework columns were
                            // squeezed to a word per line (Yuhyun, slide 7). 900px gives each
                            // column room and the table scrolls sideways in a narrow chat; the
                            // long framework name in column one wraps instead of being cut.
                            : renderDqAiMarkdown(stripMarkers(msg.text || ''), { tableMinWidth: 900, wrapFirstColumn: true });
                        if (msg.type === CHAT_TYPE.BOT && !displayedText.trim()) return null;

                        return (
                            <Box key={idx} className={`${classes.messageRow} ${isUser ? classes.userRow : classes.botRow}`}>
                                {isUser ? (
                                    <Box className={classes.userMessage}>
                                        {/* The chosen visual style keeps its picture. */}
                                        {(() => {
                                            const picked = styleForMessage(msg.text);
                                            if (!picked) return null;
                                            return (
                                                <Box
                                                    component="img"
                                                    src={picked.image}
                                                    alt={picked.label}
                                                    sx={{
                                                        width: 72, height: 72, objectFit: 'cover',
                                                        borderRadius: '8px', display: 'block', mb: '6px',
                                                        border: '2px solid rgba(255,255,255,0.7)',
                                                    }}
                                                />
                                            );
                                        })()}
                                        <div dangerouslySetInnerHTML={{ __html: displayedText }} />
                                    </Box>
                                ) : (
                                    <Box className={classes.botMsgContainer}>
                                        <Box className={classes.botMessage}>
                                            <div dangerouslySetInnerHTML={{ __html: displayedText }} />
                                            {/*
                                              * Only on the newest message. An older reply
                                              * has already been answered, and offering its
                                              * options again would send the conversation
                                              * back to a step it has left.
                                              */}
                                            {idx === chipTargetIdx && !isBotThinking && (() => {
                                                const chips = resolveChipSpec(msg, curationStage);
                                                if (!chips.items.length) return null;
                                                /*
                                                 * The visual-style question shows the style
                                                 * images instead of text chips — only once
                                                 * the reply is actually offering choices. The
                                                 * server says so (content_style) even when the
                                                 * reply only asks "Which visual style…?".
                                                 */
                                                if (msg.replyOptions?.field === 'content_style'
                                                    || (!msg.replyOptions && curationStage === 'style')) {
                                                    return (
                                                        <StyleOptions
                                                            isConsumed={false}
                                                            onSelect={(label) => sendMessage(label)}
                                                        />
                                                    );
                                                }
                                                return (
                                                    <ReplyOptions
                                                        key={idx}
                                                        items={chips.items}
                                                        multi={chips.multi}
                                                        max={chips.max}
                                                        isConsumed={false}
                                                        onSelect={(label) => sendMessage(label)}
                                                        inputText={chatInput}
                                                        setInputText={(text) => {
                                                            setChatInput(text);
                                                            if (chatInputRef && chatInputRef.current) chatInputRef.current.focus();
                                                        }}
                                                    />
                                                );
                                            })()}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                    {isBotThinking && (
                        <Box className={`${classes.messageRow} ${classes.botRow}`}>
                            <Box className={classes.botMessage} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <CircularProgress size={16} sx={{ color: '#FF6900' }} />
                                <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>Thinking...</Typography>
                            </Box>
                        </Box>
                    )}
                    <div ref={chatEndRef} />
                </Box>
            </Box>

            {/* Input area — fades in together with messages */}
            <Box className={classes.chatInputWrapper} sx={{ opacity: showContent ? 1 : 0, transition: showContent ? 'opacity 0.8s ease 0.4s' : 'none' }}>
                {isChatLocked && (
                    <Box className={`${classes.messageRow} ${classes.botRow}`} sx={{ mb: 1 }}>
                        <Box className={classes.botMsgContainer}>
                            <Box className={classes.botMessage}>
                                🎉 Your program has been built and is ready to explore! Use the tabs above to preview the learning content, assessment, and impact dashboard.
                                {canEditProgram && onEditProgram && (
                                    <>
                                        <br /><br />
                                        ✏️ To edit your program to meet your curriculum requirements, click the <strong>EDIT TOOL</strong> button below.
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Box>
                )}
                {canEditProgram && onEditProgram ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', pt: 1 }}>
                        <Tooltip
                            arrow
                            placement="top"
                            title={EDIT_TOOL_HINT}
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        background: 'linear-gradient(135deg, #052F44 0%, #0a4a6e 100%)',
                                        color: '#ffffff',
                                        fontSize: '13px',
                                        lineHeight: 1.5,
                                        px: 2,
                                        py: 1.5,
                                        maxWidth: 280,
                                        borderRadius: '10px',
                                        borderLeft: '4px solid #FF6900',
                                        boxShadow: '0 8px 32px rgba(5,47,68,0.3)',
                                    },
                                },
                                arrow: { sx: { color: '#052F44' } },
                            }}
                        >
                            <Button
                                id="edit-tool-btn"
                                onClick={() => { setEditHintOpen(false); onEditProgram(); }}
                                startIcon={<EditIcon style={{ fontSize: 18 }} />}
                                aria-label="Edit Program Information"
                                style={{
                                    background: '#EE6628', color: '#fff', flexShrink: 0,
                                    textTransform: 'none', fontWeight: 700, fontSize: '13px',
                                    borderRadius: '8px', padding: '8px 18px', whiteSpace: 'nowrap',
                                }}
                            >
                                EDIT TOOL
                            </Button>
                        </Tooltip>
                        {/* One-time showcase — the same dark tutorial bubble as the
                            Classes nudge, anchored to the LEFT of the EDIT TOOL button
                            (the button sits at the panel's right edge) so it opens inward. */}
                        {editHintOpen && (
                            <FloatingNudge
                                anchorSelector="#edit-tool-btn"
                                placement="left"
                                onDismiss={() => setEditHintOpen(false)}
                            >
                                {EDIT_TOOL_HINT}
                            </FloatingNudge>
                        )}
                    </Box>
                ) : (
                    <>
                        <TextField
                            fullWidth multiline rows={3}
                            placeholder={isChatLocked ? '' : 'Type something here'}
                            variant="outlined"
                            className={classes.chatInput}
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            onKeyDown={handleChatKeyDown}
                            disabled={inputDisabled}
                            inputRef={chatInputRef}
                        />
                        <Box className={classes.chatInputRow}>
                            <IconButton className={classes.sendBtn} size="small" onClick={handleSendMessage} disabled={inputDisabled}>
                                {isBotThinking ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <ArrowUpward sx={{ fontSize: '22px' }} />}
                            </IconButton>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default ChatSidebar;
